import "./PaymentSheetForm.css";
import React, { useState } from "react";
import { getPaymentSheet } from "../services/api";

const isoToday = () => new Date().toISOString().slice(0, 10);

const PaymentSheetForm = () => {
  const [loading, setLoading] = useState(false);

  const [orderStartDate, setOrderStartDate] = useState<string>("");
  const [orderEndDate, setOrderEndDate] = useState<string>("");
  const [rtoPercent, setRtoPercent] = useState<number | "">("");
  const [returnPercent, setReturnPercent] = useState<number | "">("");
  const [deliveredPercent, setDeliveredPercent] = useState<number | "">("");
  const [paymentDateDiffDays, setPaymentDateDiffDays] = useState<number | "">(
    ""
  );
  const [settlementDiffPercent, setSettlementDiffPercent] = useState<
    number | ""
  >("");
  const [noAds, setNoAds] = useState<number | "">("");
  const [totalAdsCost, setTotalAdsCost] = useState<number | "">("");
  const [noReferral, setNoReferral] = useState<number | "">("");
  const [totalReferralCost, setTotalReferralCost] = useState<number | "">("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (new Date(orderStartDate) > new Date(orderEndDate)) {
      alert("Start date cannot be after end date.");
      return;
    }

    if (
      Number(rtoPercent) + Number(returnPercent) + Number(deliveredPercent) !==
      100
    ) {
      alert("Total percentage must equal exactly 100%");
      return;
    }

    const payload: Record<string, string | number> = {
      startDate: orderStartDate,
      endDate: orderEndDate,
      paymentDiff: paymentDateDiffDays,
      finalSettlementPercent: settlementDiffPercent,
      noAds,
      totalAdsCost,
      noReferral,
      totalReferralCost,
      rtoPercent,
      returnPercent,
      deliveredPercent,
    };

    try {
      setLoading(true);
      await getPaymentSheet(payload);
    } catch (err: any) {
      console.error("getPaymentSheet error:", err);
      alert(err?.message ?? "Failed to generate payment sheet");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="ps-form" onSubmit={handleSubmit}>
      <div className="ps-row">
        <div className="form-group">
          <label htmlFor="orderStart">Order Start Date *</label>
          <input
            id="orderStart"
            type="date"
            required
            value={orderStartDate}
            max={isoToday()}
            onChange={(e) => setOrderStartDate(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label htmlFor="orderEnd">Order End Date *</label>
          <input
            id="orderEnd"
            type="date"
            required
            value={orderEndDate}
            max={isoToday()}
            onChange={(e) => setOrderEndDate(e.target.value)}
          />
        </div>
      </div>

      <div className="ps-row">
        <div className="form-group">
          <label htmlFor="rtoPercent">RTO Percentage (%) *</label>
          <input
            id="rtoPercent"
            type="number"
            min={0}
            max={100}
            required
            step={0.01}
            value={rtoPercent}
            onChange={(e) =>
              setRtoPercent(e.target.value === "" ? "" : Number(e.target.value))
            }
            placeholder="e.g. 50"
          />
          <small className="ps-help">Percentage of orders marked as RTO</small>
        </div>
        <div className="form-group">
          <label htmlFor="returnPercent">Return Percentage (%) *</label>
          <input
            id="returnPercent"
            type="number"
            min={0}
            max={100}
            required
            step={0.01}
            value={returnPercent}
            onChange={(e) =>
              setReturnPercent(
                e.target.value === "" ? "" : Number(e.target.value)
              )
            }
            placeholder="e.g. 30"
          />
          <small className="ps-help">
            Percentage of orders marked as Returned
          </small>
        </div>
        <div className="form-group" style={{ width: "48%" }}>
          <label htmlFor="deliveredPercent">Delivered Percentage (%) *</label>
          <input
            id="deliveredPercent"
            type="number"
            min={0}
            max={100}
            required
            step={0.01}
            value={deliveredPercent}
            onChange={(e) =>
              setDeliveredPercent(
                e.target.value === "" ? "" : Number(e.target.value)
              )
            }
            placeholder="e.g. 20"
          />
          <small className="ps-help">
            Percentage of orders marked as Delivered
          </small>
        </div>
      </div>

      <div className="ps-row">
        <div className="form-group">
          <label htmlFor="rtoPercent">Payment Date Difference (days)</label>
          <input
            id="rtoPercent"
            type="number"
            min={0}
            value={paymentDateDiffDays}
            onChange={(e) =>
              setPaymentDateDiffDays(
                e.target.value === "" ? "" : Number(e.target.value)
              )
            }
            placeholder="e.g. 15"
          />
          <small className="ps-help">
            Number of days from order date to payment date (for filtering)
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="settlementPercent">
            Final Settlement Difference (%)
          </label>
          <input
            id="settlementPercent"
            type="number"
            min={0}
            max={100}
            step={0.01}
            value={settlementDiffPercent}
            onChange={(e) =>
              setSettlementDiffPercent(
                e.target.value === "" ? "" : Number(e.target.value)
              )
            }
            placeholder="e.g. 2.5"
          />
          <small className="ps-help">
            Percentage applied when computing final settlement amounts
          </small>
        </div>
      </div>

      <div className="ps-row">
        <div className="form-group">
          <label htmlFor="noAds">Number of Ads *</label>
          <input
            id="noAds"
            type="number"
            min={0}
            required
            value={noAds}
            onChange={(e) =>
              setNoAds(e.target.value === "" ? "" : Number(e.target.value))
            }
            placeholder="e.g. 4"
          />
          <small className="ps-help">
            Number of ad runs to generate within the date range (optional)
          </small>
        </div>
        <div className="form-group">
          <label htmlFor="totalAdsCost">Total Ads Cost *</label>
          <input
            id="totalAdsCost"
            type="number"
            min={0}
            required
            value={totalAdsCost}
            onChange={(e) =>
              setTotalAdsCost(
                e.target.value === "" ? "" : Number(e.target.value)
              )
            }
            placeholder="e.g. 2000"
          />
          <small className="ps-help">
            Total amount to split across ad runs
          </small>
        </div>
      </div>

      <div className="ps-row">
        <div className="form-group">
          <label htmlFor="noReferral">Number of Referrals *</label>
          <input
            id="noReferral"
            type="number"
            min={0}
            required
            value={noReferral}
            onChange={(e) =>
              setNoReferral(e.target.value === "" ? "" : Number(e.target.value))
            }
            placeholder="e.g. 3"
          />
          <small className="ps-help">
            Number of referral payouts to generate
          </small>
        </div>
        <div className="form-group" style={{ width: "48%" }}>
          <label htmlFor="totalReferralCost">Total Referral Cost *</label>
          <input
            id="totalReferralCost"
            type="number"
            min={0}
            required
            value={totalReferralCost}
            onChange={(e) =>
              setTotalReferralCost(
                e.target.value === "" ? "" : Number(e.target.value)
              )
            }
            placeholder="e.g. 1500"
          />
          <small className="ps-help">
            Total amount to split across referral payouts
          </small>
        </div>
      </div>

      <div className="ps-actions">
        <button
          type="submit"
          className="btn-submit"
          disabled={loading}
          aria-busy={loading}
        >
          {loading ? "Generating..." : "Save Payment Sheet"}
        </button>
      </div>
    </form>
  );
};

export default PaymentSheetForm;
